import { useChannel, useRoom } from '@peterddod/phop';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_MATCH_SETTINGS,
  isMatchSettings,
  MAP_SIZE_RADIUS,
  MAP_SIZES,
  MATCH_DURATIONS,
  MAX_SEED_LENGTH,
  type MapSize,
  type MatchDuration,
  type MatchSettings,
  mapRadiusFor,
  NO_TIME_LIMIT,
} from '@/game/sim';
import { cleanName, MAX_NAME_LENGTH, randomName } from '@/lib/names';
import { ShuffleIcon } from './ConnectScreen';

const SIZE_LABELS: Record<MapSize, string> = {
  auto: 'Auto',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  huge: 'Huge',
};

function randomSeed(): string {
  return Math.random().toString(36).slice(2, 8);
}

/**
 * The admin settings: the match settings shown in the lobby, live on every
 * peer. The lobby's admin (`lib/admin.ts`: whoever created it) edits them and
 * broadcasts each change (and sends them to every peer whose channel opens);
 * everyone else keeps the latest from the current admin only. A peer that
 * becomes admin carries on from what it last saw. They're only a preview: the
 * match uses the copy `start` carries (phop `useLobby`'s config), which every
 * peer gets identically.
 */
export function useAdminSettings(
  isAdmin: boolean,
  admin: string | undefined,
  inLobby: boolean
): [MatchSettings, (next: MatchSettings) => void] {
  const { onPeerConnected } = useRoom();
  const channel = useChannel('lobby-settings', isMatchSettings);
  const [settings, setSettings] = useState<MatchSettings>(DEFAULT_MATCH_SETTINGS);
  // Read by the subscriptions without re-subscribing.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const adminRef = useRef({ isAdmin, admin, inLobby });
  adminRef.current = { isAdmin, admin, inLobby };

  // Every change, and on becoming admin.
  useEffect(() => {
    if (isAdmin && inLobby) channel.broadcast(settings);
  }, [channel, settings, isAdmin, inLobby]);

  // Peers arriving (or whose channel opened late) get the current ones.
  useEffect(
    () =>
      onPeerConnected((peer) => {
        if (adminRef.current.isAdmin && adminRef.current.inLobby) {
          channel.send(peer, settingsRef.current);
        }
      }),
    [onPeerConnected, channel]
  );

  useEffect(
    () =>
      channel.subscribe((data, senderId) => {
        const { isAdmin: self, admin: from } = adminRef.current;
        if (!self && senderId === from) setSettings(data);
      }),
    [channel]
  );

  return [settings, setSettings];
}

/**
 * Start for the admin. Only phop's lobby host may call `start`, so an admin
 * who isn't the host asks it to, with the settings to start with; the host
 * starts only for the admin (as it sees the room) and only when `canStart`
 * (every build checked) holds there too.
 */
export function useAdminStart(
  isAdmin: boolean,
  admin: string | undefined,
  isLobbyHost: boolean,
  lobbyHost: string | undefined,
  canStart: boolean,
  start: (settings: MatchSettings) => void
): (settings: MatchSettings) => void {
  const channel = useChannel('lobby-start', isMatchSettings);
  const hostRef = useRef({ admin, isLobbyHost, canStart, start });
  hostRef.current = { admin, isLobbyHost, canStart, start };

  useEffect(
    () =>
      channel.subscribe((settings, senderId) => {
        const host = hostRef.current;
        if (host.isLobbyHost && host.canStart && senderId === host.admin) host.start(settings);
      }),
    [channel]
  );

  return useCallback(
    (settings: MatchSettings) => {
      if (!isAdmin) return;
      if (isLobbyHost) start(settings);
      else if (lobbyHost) channel.send(lobbyHost, settings);
    },
    [channel, isAdmin, isLobbyHost, lobbyHost, start]
  );
}

/**
 * The lobby's match settings: length, map size and seed. The admin picks them
 * (chips and a seed field); everyone else sees them read-only.
 */
export function MatchSettingsPanel({
  settings,
  onChange,
  playerCount,
  roomId,
}: {
  settings: MatchSettings;
  /** Omitted for everyone but the admin: read-only. */
  onChange?: (next: MatchSettings) => void;
  playerCount: number;
  roomId: string;
}) {
  const radius = mapRadiusFor(settings.mapSize, playerCount);
  const players = `${playerCount} player${playerCount === 1 ? '' : 's'}`;
  return (
    <div className="mt-3 grid grid-cols-[3.25rem_1fr] items-center gap-x-2 gap-y-2 border-t border-line px-1 pt-3 text-xs">
      <span className="col-span-2 text-fg-subtle">
        Admin settings{onChange ? '' : ': only the admin can change these'}
      </span>
      <span className="text-fg-subtle">Length</span>
      <ChipRow<MatchDuration>
        options={MATCH_DURATIONS.map((m) =>
          m === NO_TIME_LIMIT
            ? {
                value: m,
                label: '∞',
                title: 'No limit: ends with the last player standing, or when the host ends it',
              }
            : { value: m, label: `${m} min` }
        )}
        value={settings.durationMinutes}
        onPick={onChange && ((durationMinutes) => onChange({ ...settings, durationMinutes }))}
      />
      <span className="text-fg-subtle">Map</span>
      <ChipRow<MapSize>
        options={MAP_SIZES.map((size) => ({
          value: size,
          label: SIZE_LABELS[size],
          title:
            size === 'auto'
              ? `Sized for the players: radius ${mapRadiusFor('auto', playerCount)} with ${players}`
              : `Radius ${MAP_SIZE_RADIUS[size]} hexes`,
        }))}
        value={settings.mapSize}
        onPick={onChange && ((mapSize) => onChange({ ...settings, mapSize }))}
      />
      <span />
      <span className="-mt-1 text-[11px] text-fg-subtle tabular-nums">
        Radius {radius} hexes{settings.mapSize === 'auto' ? ` · auto for ${players}` : ''}
      </span>
      <span className="text-fg-subtle">Seed</span>
      {onChange ? (
        <span className="flex min-w-0 items-center gap-1 rounded-full bg-fill py-0.5 pl-2.5 pr-0.5">
          <input
            aria-label="Map seed"
            className="min-w-0 flex-1 bg-transparent text-xs text-fg placeholder-fg-subtle outline-none"
            value={settings.seed}
            onChange={(e) => onChange({ ...settings, seed: e.target.value })}
            placeholder={`From the lobby name (${roomId})`}
            spellCheck={false}
            autoComplete="off"
            maxLength={MAX_SEED_LENGTH}
          />
          <button
            type="button"
            onClick={() => onChange({ ...settings, seed: randomSeed() })}
            title="Random seed"
            aria-label="Random seed"
            className="btn btn-ghost btn-icon h-6 w-6"
          >
            <ShuffleIcon />
          </button>
        </span>
      ) : (
        <span className="truncate text-fg-muted">
          {settings.seed.trim() || (
            <span className="text-fg-subtle">From the lobby name ({roomId})</span>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Your name as everyone sees it (`lib/names.ts`): any player may change
 * theirs, unlike the admin settings above it. Left blank, it gets a new
 * generated one.
 */
export function PlayerNameField({
  name,
  onChange,
}: {
  name: string;
  onChange: (name: string) => void;
}) {
  return (
    <div className="mt-3 grid grid-cols-[3.25rem_1fr] items-center gap-x-2 border-t border-line px-1 pt-3 text-xs">
      <span className="text-fg-subtle">Name</span>
      <span className="flex min-w-0 items-center gap-1 rounded-full bg-fill py-0.5 pl-2.5 pr-0.5">
        <input
          aria-label="Your name"
          className="min-w-0 flex-1 bg-transparent text-xs text-fg placeholder-fg-subtle outline-none"
          value={name}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            if (!cleanName(name)) onChange(randomName());
          }}
          placeholder="Your name"
          spellCheck={false}
          autoComplete="off"
          maxLength={MAX_NAME_LENGTH}
        />
        <button
          type="button"
          onClick={() => onChange(randomName())}
          title="Random name"
          aria-label="Random name"
          className="btn btn-ghost btn-icon h-6 w-6"
        >
          <ShuffleIcon />
        </button>
      </span>
    </div>
  );
}

function ChipRow<T extends string | number>({
  options,
  value,
  onPick,
}: {
  options: Array<{ value: T; label: string; title?: string }>;
  value: T;
  /** Omitted: read-only (the chosen chip still shows). */
  onPick?: (value: T) => void;
}) {
  return (
    <span className="flex flex-wrap gap-1">
      {options.map((o) => {
        const selected = o.value === value;
        const tone = selected ? 'chip-selected' : 'text-fg-muted';
        return onPick ? (
          <button
            key={o.value}
            type="button"
            title={o.title}
            aria-pressed={selected}
            onClick={() => onPick(o.value)}
            className={`chip transition ${tone} ${selected ? '' : 'hover:bg-fill-hover hover:text-fg'}`}
          >
            {o.label}
          </button>
        ) : (
          <span key={o.value} title={o.title} className={`chip ${tone}`}>
            {o.label}
          </span>
        );
      })}
    </span>
  );
}
